import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import MembershipPlanStep from "@/components/membership-step/MembershipPlanStep";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { trackMembershipCadenceChange } from "@/utils/analytics";

interface CadencePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: BookingLanguage;
  /**
   * Runs on Apply, before closing. The payment step uses it to select the
   * member option — picking a cadence there is an explicit membership choice.
   * The cart doesn't need it: the membership is already attached, so applying
   * only confirms a cadence that is live in the store either way.
   */
  onApply?: () => void;
  /** Which of the two entry points opened it — reported with the change. */
  source: "cart" | "payment_step";
}

/**
 * "Change cadence" picker — reuses the testing-rhythm step.
 *
 * Plain overlay rather than the Radix dialog, to avoid the body pointer-events
 * lock and to keep it self-contained. `autoSeed={false}` so opening it keeps
 * the current cadence selection instead of re-seeding the default.
 *
 * Lives in its own file because two places open it: the payment step's member
 * option, and the membership line in the order summary (where the cadence is
 * otherwise a dead end — you could see "1 advanced blood test" but not change
 * it without going back through the flow).
 */
export const CadencePickerModal: React.FC<CadencePickerModalProps> = ({
  isOpen,
  onClose,
  language,
  onApply,
  source,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const plan = useMembershipPlanStore((s) => s.plan);

  // The plan the user arrived with. MembershipPlanStep writes straight to the
  // store, so by the time Apply is pressed the "before" value is already gone —
  // capture it on open, and report the pair so a no-op Apply is distinguishable
  // from a real upgrade.
  const planOnOpen = useRef<string | null>(null);
  useEffect(() => {
    if (isOpen) planOnOpen.current = plan;
    // `plan` is deliberately not a dependency: this must latch the value at
    // open time, not follow it as the user clicks through the tiers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start justify-center p-4 sm:items-center">
        <div
          className="relative w-full max-w-2xl my-8 rounded-2xl bg-white p-6 shadow-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={t("paymentInfo.closeCadence")}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-neutral-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <MembershipPlanStep autoSeed={false} />
          <div className="mx-auto mt-6 w-full max-w-[560px]">
            <button
              type="button"
              onClick={() => {
                trackMembershipCadenceChange(planOnOpen.current, plan, source);
                onApply?.();
                onClose();
              }}
              className="w-full rounded-full bg-aubergine-800 hover:bg-aubergine-800/90 text-white py-3.5 font-semibold text-base normal-case transition-colors"
            >
              {t("paymentInfo.applyCadence")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadencePickerModal;
